// Dashboard page shell — FE-031 / spec §7.0 + dashboard parity v1.
//
// Page structure mirrors the v1 dashboard
// (``benecrm-mega-sync/.../dashboard/analytics.tsx``) with one
// variation: the Cumulative Flow Diagram sits directly under the
// "Employee Analytics" group title (v1 placed it at the bottom).
// The two visible groups — Employee Analytics and Account
// Analytics — are full-width stacked sections inside a single
// max-width container; cross-cutting filters live in
// ``DashboardHeader`` above the groups.

import {
  AccountPerformanceSection,
  GoingColdSection,
  TouchpointHealthSection,
} from "./accountPerformance";
import {
  AccountAnalyticsSection,
  RiskWorkloadSection,
} from "./accountRisk";
import { CfdSection } from "./cfd";
import { EmployeeAnalyticsSection } from "./employeeAnalytics";
import { RenewalPipelineSection } from "./renewalPipeline";
import {
  BlockingTasksSection,
  EmployeeDeliverySection,
  EmployeePerformanceSection,
  MostOverdueSection,
  TaskCompletionTrendSection,
  TeamCapacitySection,
} from "./staffPerformance";
import { DashboardHeader } from "./DashboardHeader";
import { DashboardFilterProvider } from "./filterState";
import { HealthKpiBar } from "./HealthKpiBar";


export function DashboardPage() {
  return (
    <DashboardFilterProvider>
      <div className="flex flex-col gap-8">
        <DashboardHeader />
        {/* Broker-wide health pills sit between the header and
            the Employee Analytics group — same slot v1 had. */}
        <HealthKpiBar />

        {/* ── Employee Analytics ─────────────────────────────── */}
        <section
          data-testid="dashboard-employee-analytics-group"
          className="flex flex-col gap-4"
        >
          <header>
            <h2 className="text-base font-semibold tracking-tight text-text-primary">
              Employee Analytics
            </h2>
          </header>
          {/* CFD is the first chart in the group per the parity
              spec — gives an at-a-glance read on overall
              throughput before drilling into per-staff metrics. */}
          <CfdSection />
          <EmployeeAnalyticsSection />
          <TeamCapacitySection />
          <BlockingTasksSection />
          <EmployeeDeliverySection />
          <TaskCompletionTrendSection />
          <MostOverdueSection />
          <EmployeePerformanceSection />
        </section>

        {/* ── Account Analytics ─────────────────────────────── */}
        <section
          data-testid="dashboard-account-analytics-group"
          className="flex flex-col gap-4"
        >
          <header>
            <h2 className="text-base font-semibold tracking-tight text-text-primary">
              Account Analytics
            </h2>
          </header>
          <AccountAnalyticsSection />
          <RenewalPipelineSection />
          <TouchpointHealthSection />
          <RiskWorkloadSection />
          <AccountPerformanceSection />
          <GoingColdSection />
        </section>
      </div>
    </DashboardFilterProvider>
  );
}


// Re-export the (now empty) placeholder slug list. Kept exported
// so route-level smoke tests can assert the dashboard has no
// remaining FE-031 placeholders without importing from inside the
// page module.
export const DASHBOARD_PLACEHOLDER_SECTIONS: string[] = [];
